import { useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useSession } from '../lib/session'
import { usePlans } from '../lib/queries'
import { Avatar, Sheet } from './ui'
import { CalendarIcon, HomeIcon, PeopleIcon } from './Icons'

const TABS = [
  { to: '/', label: 'Today', Icon: HomeIcon, end: true },
  { to: '/week', label: 'Week', Icon: CalendarIcon, end: false },
  { to: '/family', label: 'Family', Icon: PeopleIcon, end: false },
]

function todayLabel(timezone: string | undefined): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
  }).format(new Date())
}

export function AppShell() {
  const { household, session, members, isParent, viewedMemberId, setViewedMemberId, signOut } = useSession()
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const navigate = useNavigate()
  const { data: plans } = usePlans('upcoming')

  const pendingPlans = plans?.filter((plan) => plan.status === 'requested').length ?? 0
  const viewing = members.find((m) => m.id === viewedMemberId)
  const viewingSomeoneElse = isParent && viewedMemberId !== session?.memberId

  return (
    <div className="shell">
      <header className="appbar">
        <div>
          <div className="appbar-title">{household?.name ?? 'Brighton'}</div>
          <div className="faint">{todayLabel(household?.timezone)}</div>
        </div>

        <button
          type="button"
          className="btn btn-quiet"
          onClick={() => setSwitcherOpen(true)}
          aria-label="Account and family member"
        >
          {viewingSomeoneElse && viewing ? (
            <span className="pill pill-accent">Viewing {viewing.name}</span>
          ) : null}
          <Avatar emoji={session?.emoji ?? '🙂'} color={session?.color} ring />
        </button>
      </header>

      <Outlet />

      <nav className="tabbar" aria-label="Main">
        {TABS.map(({ to, label, Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `tab${isActive ? ' tab-active' : ''}`}
          >
            <Icon />
            <span>{label}</span>
            {label === 'Family' && pendingPlans > 0 ? <span className="tab-dot" /> : null}
          </NavLink>
        ))}
      </nav>

      <Sheet open={switcherOpen} title={session?.name ?? 'Menu'} onClose={() => setSwitcherOpen(false)}>
        {isParent ? (
          <div className="stack">
            <p className="section-title">View someone's lists</p>
            <div className="list">
              {members.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  className={`check${member.id === viewedMemberId ? ' check-done' : ''}`}
                  onClick={() => {
                    setViewedMemberId(member.id === session?.memberId ? null : member.id)
                    setSwitcherOpen(false)
                  }}
                >
                  <Avatar emoji={member.emoji} color={member.color} ring />
                  <span className="check-body">
                    <span className="check-title">{member.name}</span>
                    <span className="check-detail">{member.role === 'parent' ? 'Parent' : 'Kid'}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="stack">
          {isParent ? (
            <button
              type="button"
              className="btn btn-ghost btn-block"
              onClick={() => {
                setSwitcherOpen(false)
                navigate('/settings')
              }}
            >
              Settings
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost btn-block"
            onClick={() => {
              setSwitcherOpen(false)
              navigate('/agreement')
            }}
          >
            Our family agreement
          </button>
          <button
            type="button"
            className="btn btn-danger btn-block"
            onClick={() => {
              setSwitcherOpen(false)
              void signOut().then(() => navigate('/signin', { replace: true }))
            }}
          >
            Sign out
          </button>
        </div>
      </Sheet>
    </div>
  )
}
